import React, { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { CheckCircleIcon, CalendarIcon, ClockIcon, MapPinIcon } from '@heroicons/react/24/solid';
import { resolveImageUrl } from '../utils/formatters';

const BookingConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { booking } = location.state || {};
  const [serviceImageError, setServiceImageError] = useState(false);
  const [staffImageError, setStaffImageError] = useState(false);
  const [businessImageError, setBusinessImageError] = useState(false);

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600 mb-4">No booking information found.</p>
          <button 
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const bookingDateStr = booking.booking_date_local
    ? booking.booking_date_local
    : (typeof booking.booking_date === 'string'
        ? booking.booking_date.split('T')[0]
        : new Date(booking.booking_date).toISOString().split('T')[0]);

  const bookingTimeStr = booking.end_time_local
    ? `${booking.start_time_local} - ${booking.end_time_local}`
    : (booking.start_time_local || booking.start_time);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-primary-600 px-6 py-8 text-center">
          <CheckCircleIcon className="h-16 w-16 text-white mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white">Booking Confirmed!</h1>
          <p className="text-primary-100 mt-2">Your appointment has been successfully scheduled.</p>
        </div>
        
        <div className="p-8">
          <div className="space-y-6">
            <div className="border-b pb-6">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Business Details</h2>
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {booking.business_profile_image && !businessImageError ? (
                    <img
                      src={resolveImageUrl(booking.business_profile_image)}
                      alt={booking.business_name}
                      className="h-10 w-10 rounded-lg object-cover border bg-white"
                      onError={() => setBusinessImageError(true)}
                    />
                  ) : (
                    <span className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-primary-100 text-primary-600 text-xl">
                      🏢
                    </span>
                  )}
                </div>
                <div className="ml-4">
                  <p className="text-lg font-bold text-gray-900">{booking.business_name}</p>
                  <div className="mt-1 flex items-center gap-2">
                    {booking.service_image && !serviceImageError ? (
                      <img
                        src={resolveImageUrl(booking.service_image)}
                        alt={booking.service_name}
                        className="h-6 w-6 rounded object-cover border bg-white"
                        onError={() => setServiceImageError(true)}
                      />
                    ) : null}
                    <p className="text-primary-600 font-medium">{booking.service_name}</p>
                  </div>
                  {booking.business_address && (
                    <p className="text-sm text-gray-500 mt-1 flex items-center">
                      <MapPinIcon className="h-4 w-4 mr-1 text-gray-400" />
                      {booking.business_address}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {booking.staff_name && (
              <div className="border-b pb-6">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Staff</h2>
                <div className="flex items-center gap-3">
                  {booking.staff_profile_image && !staffImageError ? (
                    <img
                      src={resolveImageUrl(booking.staff_profile_image)}
                      alt={booking.staff_name}
                      className="h-10 w-10 rounded-full object-cover border bg-white"
                      onError={() => setStaffImageError(true)}
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full border bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-500">
                      {(booking.staff_name || 'S').slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-gray-900 font-bold truncate">{booking.staff_name}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Time & Date</h2>
              
              <div className="flex items-center text-gray-700">
                <CalendarIcon className="h-5 w-5 text-primary-500 mr-3" />
                <span className="font-medium">{new Date(`${bookingDateStr}T00:00:00`).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</span>
              </div>

              <div className="flex items-center text-gray-700">
                <ClockIcon className="h-5 w-5 text-primary-500 mr-3" />
                <span className="font-medium">
                  {bookingTimeStr}
                </span>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mt-6">
              <div className="flex items-start">
                <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-gray-600 italic">
                    A confirmation email has been sent to your registered address.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-6 flex flex-col space-y-3">
              <Link 
                to="/bookings" 
                className="w-full btn-primary text-center py-3"
              >
                View My Bookings
              </Link>
              <Link 
                to="/" 
                className="w-full text-center text-primary-600 font-medium hover:text-primary-700 py-2"
              >
                Return to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
